import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, tareasService, cursosService } from '../services/api';
import ConfirmPopup from '../components/ConfirmPopup';
import './Grupos.css';

const Grupos = () => {
  const navigate = useNavigate();
  const [cursos, setCursos] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [selectedCursoId, setSelectedCursoId] = useState('');
  const [selectedTareaId, setSelectedTareaId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingGrupos, setLoadingGrupos] = useState(false);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [configNumGrupos, setConfigNumGrupos] = useState('');
  const [configMaxIntegrantes, setConfigMaxIntegrantes] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);
  const [movingUser, setMovingUser] = useState(null);
  const [togglingActivo, setTogglingActivo] = useState(null);
  const [togglingInscripcion, setTogglingInscripcion] = useState(false);
  const [dragOverTarget, setDragOverTarget] = useState(null); // 'sin' | `grupo-${id}`
  const [draggingUserId, setDraggingUserId] = useState(null);

  const loadCursos = useCallback(async () => {
    try {
      const list = await cursosService.getCursos();
      // Solo cursos en los que estoy asignado (equipo)
      const filtrado = (list || []).filter(curso => {
        if (!currentUser || !curso.equipo || !Array.isArray(curso.equipo)) return false;
        return curso.equipo.some(m => {
          const uid = typeof m === 'object' && m && (m.id != null || m.user_id != null) ? (m.user_id ?? m.id) : m;
          return parseInt(uid) === parseInt(currentUser.id);
        });
      });
      setCursos(filtrado);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar cursos');
      setCursos([]);
    }
  }, [currentUser]);

  const loadTareas = useCallback(async () => {
    try {
      const list = await tareasService.getTareas();
      setTareas(list || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar tareas');
      setTareas([]);
    }
  }, []);

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }
    const user = authService.getCurrentUser();
    setCurrentUser(user);
  }, [navigate]);

  useEffect(() => {
    if (currentUser === null) return;
    setLoading(true);
    setError('');
    Promise.all([loadCursos(), loadTareas()]).finally(() => setLoading(false));
  }, [currentUser, loadCursos, loadTareas]);

  const tareasConGruposDelCurso = (selectedCursoId && tareas.length)
    ? tareas.filter(t => t.entrega_en_grupos && String(t.curso) === String(selectedCursoId))
    : [];

  useEffect(() => {
    if (selectedCursoId && !tareasConGruposDelCurso.some(t => String(t.id) === String(selectedTareaId))) {
      setSelectedTareaId('');
    }
    setData(null);
  }, [selectedCursoId, tareas, selectedTareaId]);

  useEffect(() => {
    if (!selectedTareaId) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoadingGrupos(true);
    setError('');
    tareasService.getGruposTarea(selectedTareaId)
      .then(res => {
        if (!cancelled) {
          setData(res);
          setConfigNumGrupos(res.tarea?.num_grupos != null ? String(res.tarea.num_grupos) : '');
          setConfigMaxIntegrantes(res.tarea?.max_integrantes_por_grupo != null ? String(res.tarea.max_integrantes_por_grupo) : '');
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err.response?.data?.error || 'Error al cargar grupos');
          setData(null);
        }
      })
      .finally(() => { if (!cancelled) setLoadingGrupos(false); });
    return () => { cancelled = true; };
  }, [selectedTareaId]);

  const handleAplicarConfig = async () => {
    if (!selectedTareaId) return;
    const num = configNumGrupos.trim() === '' ? null : parseInt(configNumGrupos, 10);
    const max = configMaxIntegrantes.trim() === '' ? null : parseInt(configMaxIntegrantes, 10);
    if (num !== null && (isNaN(num) || num < 0)) {
      setError('Cantidad de grupos debe ser un número >= 0.');
      return;
    }
    if (max !== null && (isNaN(max) || max < 1)) {
      setError('Máx. integrantes debe ser un número >= 1.');
      return;
    }
    setSavingConfig(true);
    setError('');
    try {
      await tareasService.configGruposTarea(selectedTareaId, {
        num_grupos: num ?? 0,
        max_integrantes_por_grupo: max,
      });
      const res = await tareasService.getGruposTarea(selectedTareaId);
      setData(res);
      setConfigNumGrupos(res.tarea?.num_grupos != null ? String(res.tarea.num_grupos) : '');
      setConfigMaxIntegrantes(res.tarea?.max_integrantes_por_grupo != null ? String(res.tarea.max_integrantes_por_grupo) : '');
    } catch (err) {
      setError(err.response?.data?.error || 'Error al aplicar configuración');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleAgregarAGrupo = async (grupoId, userId) => {
    if (!selectedTareaId || !userId) return;
    if (data?.tarea?.grupos_solo_lectura || data?.tarea?.hereda_grupos) {
      setError('Los integrantes solo se pueden modificar en la tarea origen de los grupos.');
      return;
    }
    setMovingUser(userId);
    setError('');
    try {
      await tareasService.agregarAGrupo(selectedTareaId, grupoId, userId);
      const res = await tareasService.getGruposTarea(selectedTareaId);
      setData(res);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al agregar al grupo');
    } finally {
      setMovingUser(null);
    }
  };

  const handleQuitarDeGrupo = async (grupoId, userId) => {
    if (!selectedTareaId || !userId) return;
    if (data?.tarea?.grupos_solo_lectura || data?.tarea?.hereda_grupos) {
      setError('Los integrantes solo se pueden modificar en la tarea origen de los grupos.');
      return;
    }
    setMovingUser(userId);
    setError('');
    try {
      await tareasService.quitarDeGrupo(selectedTareaId, grupoId, userId);
      const res = await tareasService.getGruposTarea(selectedTareaId);
      setData(res);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al quitar del grupo');
    } finally {
      setMovingUser(null);
    }
  };

  const handleToggleActivo = async (grupoId, nuevoActivo) => {
    if (!selectedTareaId) return;
    setTogglingActivo(grupoId);
    setError('');
    try {
      await tareasService.actualizarGrupoTarea(selectedTareaId, grupoId, { activo: nuevoActivo });
      const res = await tareasService.getGruposTarea(selectedTareaId);
      setData(res);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cambiar estado del grupo');
    } finally {
      setTogglingActivo(null);
    }
  };

  const handleToggleInscripcion = async () => {
    if (!selectedTareaId || !data?.tarea) return;
    const cerrar = !data.tarea.grupos_cerrados;
    setTogglingInscripcion(true);
    setError('');
    try {
      await tareasService.setGruposInscripcion(selectedTareaId, cerrar);
      const res = await tareasService.getGruposTarea(selectedTareaId);
      setData(res);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cambiar inscripción de grupos');
    } finally {
      setTogglingInscripcion(false);
    }
  };

  const parseDragPayload = (e) => {
    try {
      const raw = e.dataTransfer.getData('application/json') || e.dataTransfer.getData('text/plain');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || parsed.userId == null) return null;
      return {
        userId: parseInt(parsed.userId, 10),
        fromGrupoId: parsed.fromGrupoId != null ? parseInt(parsed.fromGrupoId, 10) : null,
      };
    } catch (_) {
      return null;
    }
  };

  const handleDragStartEstudiante = (e, userId, fromGrupoId = null) => {
    const soloLectura = Boolean(data?.tarea?.grupos_solo_lectura || data?.tarea?.hereda_grupos);
    if (movingUser != null || soloLectura) {
      e.preventDefault();
      return;
    }
    const payload = JSON.stringify({ userId, fromGrupoId });
    e.dataTransfer.setData('application/json', payload);
    e.dataTransfer.setData('text/plain', payload);
    e.dataTransfer.effectAllowed = 'move';
    setDraggingUserId(userId);
  };

  const handleDragEndEstudiante = () => {
    setDraggingUserId(null);
    setDragOverTarget(null);
  };

  const handleDragOverZona = (e, targetKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverTarget !== targetKey) setDragOverTarget(targetKey);
  };

  const handleDragLeaveZona = (e, targetKey) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverTarget((prev) => (prev === targetKey ? null : prev));
    }
  };

  const handleDropEnGrupo = async (e, grupoId) => {
    e.preventDefault();
    setDragOverTarget(null);
    setDraggingUserId(null);
    if (data?.tarea?.grupos_solo_lectura || data?.tarea?.hereda_grupos) return;
    const payload = parseDragPayload(e);
    if (!payload || !payload.userId) return;
    if (payload.fromGrupoId != null && payload.fromGrupoId === grupoId) return;

    const grupo = (data?.grupos || []).find((g) => g.id === grupoId);
    const max = data?.tarea?.max_integrantes_por_grupo;
    const count = grupo?.integrantes?.length || 0;
    const alreadyIn = (grupo?.integrantes || []).some((i) => i.id === payload.userId);
    if (!alreadyIn && max != null && count >= max) {
      setError(`Este grupo admite como máximo ${max} integrantes.`);
      return;
    }
    await handleAgregarAGrupo(grupoId, payload.userId);
  };

  const handleDropEnSinAsignar = async (e) => {
    e.preventDefault();
    setDragOverTarget(null);
    setDraggingUserId(null);
    if (data?.tarea?.grupos_solo_lectura || data?.tarea?.hereda_grupos) return;
    const payload = parseDragPayload(e);
    if (!payload || !payload.userId || payload.fromGrupoId == null) return;
    await handleQuitarDeGrupo(payload.fromGrupoId, payload.userId);
  };

  if (loading) {
    return (
      <div className="grupos-container">
        <div className="grupos-loading">
          <div className="loading-spinner"></div>
          <p>Cargando...</p>
        </div>
      </div>
    );
  }

  const gruposSoloLectura = Boolean(data?.tarea?.grupos_solo_lectura || data?.tarea?.hereda_grupos);
  const fuenteTareaId = data?.tarea?.grupos_fuente_tarea_id;
  const fuenteTareaTitulo = data?.tarea?.grupos_fuente_titulo;

  return (
    <div className="grupos-container">
      <header className="grupos-header">
        <h1>Gestión de grupos</h1>
        <p className="grupos-subtitle">Configurá grupos para tareas con entrega en grupos y asigná integrantes.</p>
      </header>

      <ConfirmPopup
        open={Boolean(error)}
        title="Error"
        message={error}
        confirmLabel="Aceptar"
        cancelLabel={null}
        onConfirm={() => setError('')}
        onCancel={() => setError('')}
        variant="danger"
      />

      <div className="grupos-selectors">
        <div className="grupos-form-group">
          <label htmlFor="grupos-curso">Curso</label>
          <select
            id="grupos-curso"
            className="grupos-select"
            value={selectedCursoId}
            onChange={(e) => setSelectedCursoId(e.target.value)}
          >
            <option value="">Seleccionar curso</option>
            {(cursos || []).map(c => (
              <option key={c.id} value={c.id}>{c.nombre}</option>
            ))}
          </select>
        </div>
        <div className="grupos-form-group">
          <label htmlFor="grupos-tarea">Tarea (entrega en grupos)</label>
          <select
            id="grupos-tarea"
            className="grupos-select"
            value={selectedTareaId}
            onChange={(e) => setSelectedTareaId(e.target.value)}
            disabled={!selectedCursoId}
          >
            <option value="">Seleccionar tarea</option>
            {tareasConGruposDelCurso.map(t => (
              <option key={t.id} value={t.id}>
                {t.titulo}{t.reutilizar_grupos_de ? ' (comparte grupos)' : ''}
              </option>
            ))}
            {selectedCursoId && tareasConGruposDelCurso.length === 0 && (
              <option value="" disabled>No hay tareas con entrega en grupos</option>
            )}
          </select>
        </div>
      </div>

      {selectedTareaId && (
        <>
          {loadingGrupos ? (
            <div className="grupos-loading-inline">
              <div className="loading-spinner"></div>
              <span>Cargando grupos...</span>
            </div>
          ) : data ? (
            <>
              {gruposSoloLectura && fuenteTareaId && (
                <div className="grupos-aviso-compartidos" role="status">
                  Esta tarea comparte grupos con <strong>{fuenteTareaTitulo || 'otra actividad'}</strong>.
                  Solo podés ver los integrantes acá; para agregar o quitar estudiantes andá a la tarea origen.
                  {String(fuenteTareaId) !== String(selectedTareaId) && (
                    <button
                      type="button"
                      className="grupos-btn-ir-origen"
                      onClick={() => setSelectedTareaId(String(fuenteTareaId))}
                    >
                      Ir a la tarea origen
                    </button>
                  )}
                </div>
              )}
              <div className="grupos-config-card">
                <h2 className="grupos-config-title">Configuración</h2>
                <p className="grupos-tarea-info">
                  <strong>{data.tarea?.titulo}</strong>
                  {data.tarea?.curso_nombre && ` · ${data.tarea.curso_nombre}`}
                </p>
                <div className="grupos-config-fields">
                  <div className="grupos-form-group">
                    <label htmlFor="grupos-num">Cantidad de grupos</label>
                    <input
                      id="grupos-num"
                      type="number"
                      min="0"
                      className="grupos-input"
                      value={configNumGrupos}
                      onChange={(e) => setConfigNumGrupos(e.target.value)}
                      placeholder="Ej: 5"
                      disabled={gruposSoloLectura}
                    />
                  </div>
                  <div className="grupos-form-group">
                    <label htmlFor="grupos-max">Máx. integrantes por grupo</label>
                    <input
                      id="grupos-max"
                      type="number"
                      min="1"
                      className="grupos-input"
                      value={configMaxIntegrantes}
                      onChange={(e) => setConfigMaxIntegrantes(e.target.value)}
                      placeholder="Ej: 4"
                      disabled={gruposSoloLectura}
                    />
                  </div>
                  <div className="grupos-form-group grupos-form-group-btn">
                    <button
                      type="button"
                      className="grupos-btn-aplicar"
                      onClick={handleAplicarConfig}
                      disabled={savingConfig || gruposSoloLectura}
                    >
                      {savingConfig ? 'Aplicando…' : 'Aplicar'}
                    </button>
                  </div>
                </div>
                <div className="grupos-inscripcion-actions">
                  {data.tarea?.grupos_cerrados ? (
                    <>
                      <span className="grupos-inscripcion-badge grupos-inscripcion-badge--cerrada">
                        Inscripción cerrada — los estudiantes no pueden unirse por su cuenta
                      </span>
                      <button
                        type="button"
                        className="grupos-btn-reabrir-inscripcion"
                        onClick={handleToggleInscripcion}
                        disabled={togglingInscripcion}
                      >
                        {togglingInscripcion ? 'Procesando…' : 'Reabrir inscripción'}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="grupos-btn-cerrar-inscripcion"
                      onClick={handleToggleInscripcion}
                      disabled={togglingInscripcion}
                    >
                      {togglingInscripcion ? 'Procesando…' : 'Cerrar inscripción a grupos'}
                    </button>
                  )}
                  <p className="grupos-inscripcion-hint">
                    Al cerrar, los estudiantes dejan de poder unirse desde Mis grupos. Vos podés seguir asignándolos manualmente.
                  </p>
                </div>
              </div>

              <div className={`grupos-grid${gruposSoloLectura ? ' grupos-grid--solo-lectura' : ''}`}>
                <div
                  className={`grupos-col grupos-col-sin${!gruposSoloLectura && dragOverTarget === 'sin' ? ' grupos-drop-active' : ''}`}
                  onDragOver={gruposSoloLectura ? undefined : (e) => handleDragOverZona(e, 'sin')}
                  onDragLeave={gruposSoloLectura ? undefined : (e) => handleDragLeaveZona(e, 'sin')}
                  onDrop={gruposSoloLectura ? undefined : handleDropEnSinAsignar}
                >
                  <h3 className="grupos-col-title">Sin asignar</h3>
                  <p className="grupos-dnd-hint">
                    {gruposSoloLectura
                      ? 'Solo lectura: para mover estudiantes usá «Ir a la tarea origen».'
                      : 'Arrastrá un estudiante a un grupo. También podés soltar acá para quitarlo.'}
                  </p>
                  <ul className="grupos-lista-integrantes">
                    {(data.estudiantes_sin_grupo || []).length === 0 ? (
                      <li className="grupos-empty">Ningún estudiante sin asignar</li>
                    ) : (
                      (data.estudiantes_sin_grupo || []).map((est) => (
                        <li
                          key={est.id}
                          className={`grupos-integrante${!gruposSoloLectura ? ' grupos-integrante--draggable' : ''}${draggingUserId === est.id ? ' grupos-integrante--dragging' : ''}${movingUser === est.id ? ' grupos-integrante--busy' : ''}`}
                          draggable={!gruposSoloLectura && movingUser == null}
                          onDragStart={gruposSoloLectura ? undefined : (e) => handleDragStartEstudiante(e, est.id, null)}
                          onDragEnd={gruposSoloLectura ? undefined : handleDragEndEstudiante}
                        >
                          {!gruposSoloLectura && <span className="grupos-integrante-grip" aria-hidden>⋮⋮</span>}
                          <span className="grupos-integrante-nombre">{est.nombre || est.username}</span>
                        </li>
                      ))
                    )}
                  </ul>
                </div>

                <div className="grupos-col grupos-col-grupos">
                  <h3 className="grupos-col-title">Grupos</h3>
                  <div className="grupos-cards">
                    {(data.grupos || []).map((grupo) => {
                      const count = grupo.integrantes?.length || 0;
                      const max = data.tarea?.max_integrantes_por_grupo;
                      const full = max != null && count >= max;
                      const dropKey = `grupo-${grupo.id}`;
                      return (
                      <div
                        key={grupo.id}
                        className={`grupos-card${grupo.activo === false ? ' grupos-card--inactivo' : ''}${!gruposSoloLectura && dragOverTarget === dropKey ? ' grupos-drop-active' : ''}${full ? ' grupos-card--full' : ''}`}
                        onDragOver={gruposSoloLectura ? undefined : (e) => handleDragOverZona(e, dropKey)}
                        onDragLeave={gruposSoloLectura ? undefined : (e) => handleDragLeaveZona(e, dropKey)}
                        onDrop={gruposSoloLectura ? undefined : (e) => handleDropEnGrupo(e, grupo.id)}
                      >
                        <div className="grupos-card-header">
                          <span className="grupos-card-nombre">{grupo.nombre}</span>
                          {grupo.ingreso_cerrado && (
                            <span className="grupos-card-ingreso-cerrado">Ingreso cerrado por el grupo</span>
                          )}
                          {max != null && (
                            <span className="grupos-card-count">
                              {count} / {max}
                            </span>
                          )}
                          <label className="grupos-card-activo">
                            <input
                              type="checkbox"
                              checked={grupo.activo !== false}
                              disabled={togglingActivo === grupo.id || gruposSoloLectura}
                              onChange={(e) => handleToggleActivo(grupo.id, e.target.checked)}
                              aria-label={grupo.activo !== false ? 'Grupo activo (desmarcar para desactivar sala de chat)' : 'Grupo inactivo (marcar para activar sala de chat)'}
                            />
                            <span className="grupos-card-activo-label">Activo</span>
                          </label>
                        </div>
                        <ul className="grupos-lista-integrantes grupos-lista-integrantes--dropzone">
                          {(grupo.integrantes || []).length === 0 ? (
                            <li className={`grupos-empty${!gruposSoloLectura ? ' grupos-empty--drop' : ''}`}>
                              {gruposSoloLectura ? 'Sin integrantes' : 'Soltá acá un estudiante'}
                            </li>
                          ) : (
                            (grupo.integrantes || []).map((int) => (
                              <li
                                key={int.id}
                                className={`grupos-integrante${!gruposSoloLectura ? ' grupos-integrante--draggable' : ''}${draggingUserId === int.id ? ' grupos-integrante--dragging' : ''}${movingUser === int.id ? ' grupos-integrante--busy' : ''}`}
                                draggable={!gruposSoloLectura && movingUser == null}
                                onDragStart={gruposSoloLectura ? undefined : (e) => handleDragStartEstudiante(e, int.id, grupo.id)}
                                onDragEnd={gruposSoloLectura ? undefined : handleDragEndEstudiante}
                              >
                                {!gruposSoloLectura && <span className="grupos-integrante-grip" aria-hidden>⋮⋮</span>}
                                <span className="grupos-integrante-nombre">{int.nombre || int.username}</span>
                                {!gruposSoloLectura && (
                                  <button
                                    type="button"
                                    className="grupos-btn-quitar"
                                    onClick={() => handleQuitarDeGrupo(grupo.id, int.id)}
                                    disabled={movingUser === int.id}
                                    title="Quitar del grupo"
                                  >
                                    Quitar
                                  </button>
                                )}
                              </li>
                            ))
                          )}
                        </ul>
                      </div>
                      );
                    })}
                    {(data.grupos || []).length === 0 && (
                      <p className="grupos-empty-msg">Aplicá cantidad de grupos arriba para crear los grupos.</p>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </>
      )}

      {!selectedTareaId && selectedCursoId && tareasConGruposDelCurso.length === 0 && (
        <p className="grupos-empty-msg">Este curso no tiene tareas con entrega en grupos. Editá una tarea y marcá “Entrega en grupos”.</p>
      )}
    </div>
  );
};

export default Grupos;
